import {
  DarkTheme,
  DefaultTheme,
  ThemeProvider as NavigationThemeProvider,
} from '@react-navigation/native';
import { GluestackUIProvider } from '@vidahealth/vida-ui';
import { colorScheme as nativewindColorScheme, vars } from 'nativewind';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Platform, useColorScheme, View } from 'react-native';

import { onboardingTheme } from '../onboardingTheme';

type AppThemeProviderProps = {
  children: ReactNode;
};

type ColorMode = 'light' | 'dark';

const getWebColorMode = (): ColorMode => {
  if (typeof document !== 'undefined' && document.documentElement.classList.contains('dark')) {
    return 'dark';
  }

  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }

  return 'light';
};

export function AppThemeProvider({ children }: AppThemeProviderProps) {
  const scheme = useColorScheme();
  const nativeMode = scheme === 'dark' ? 'dark' : 'light';
  const [mode, setMode] = useState<ColorMode>(() => {
    if (Platform.OS === 'web') return getWebColorMode();
    return nativeMode;
  });

  const themeVars = useMemo(() => vars(onboardingTheme.modes[mode].cssVariables), [mode]);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      setMode(nativeMode);
      return;
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)');

    // Detect via the media query, then push the result into nativewind's color
    // scheme. vida-ui's GluestackUIProvider reads nativewind's `useColorScheme()`
    // to choose the CSS variables it stamps on the `display:contents` wrapper
    // that hosts gluestack portals (Actionsheet/BottomSheet/Select). The dev
    // runtime tracks the system automatically, but the production web export
    // leaves nativewind at 'light' unless we set it — which made portals render
    // the light palette over a dark page (e.g. the existing-account sheet).
    // (class = how dark mode is applied; media = how we detect it.)
    const syncWebMode = () => {
      const next: ColorMode = media.matches ? 'dark' : 'light';
      setMode(next);
      nativewindColorScheme.set(next);
    };

    syncWebMode();
    media.addEventListener('change', syncWebMode);

    return () => media.removeEventListener('change', syncWebMode);
  }, [nativeMode]);

  return (
    <GluestackUIProvider mode='system' theme={onboardingTheme}>
      <NavigationThemeProvider value={mode === 'dark' ? DarkTheme : DefaultTheme}>
        <View className='h-full w-full flex-1' style={themeVars}>
          {children}
        </View>
      </NavigationThemeProvider>
    </GluestackUIProvider>
  );
}
